import { CREATE_PROFILE_REDUCER } from '@/constants'
import {
  businessInformationPanelId,
  profileDescriptionPanelId,
  profilePhotosPanelId,
} from '@/screens/create-profile-page/constants'
import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import {
  FormData,
  InputKeys,
  InputValue,
  PanelInput,
  ProfileDetails,
  initialCreateProfileReduxStateTypes,
} from './types'

export const initialCreateProfileReduxState: initialCreateProfileReduxStateTypes =
  {
    currentPanelIndex: 0,
    pageIsLoading: false,
    profileDetailsAreLoading: false,
    errorMessage: '',
    unmappedErrorOccurred: false,
    retryUnmappedError: false,
    profileDetails: {
      businessName: '',
      businessAddress: {},
      contactInfo: {},
    } as ProfileDetails,
    formData: {
      [profileDescriptionPanelId]: {
        businessHandle: { value: '', errors: [], isValid: false },
        businessDescription: { value: '', errors: [], isValid: true },
      },
      [businessInformationPanelId]: {
        selectedLocationDisplayPreference: {
          value: '',
          errors: [],
          isValid: true,
        },
        selectedContactInfo: {
          value: {},
          errors: [],
          isValid: false,
        },
      },
      [profilePhotosPanelId]: {
        profilePhoto: {
          value: '',
          errors: [],
          isValid: true,
        },
        coverPhoto: {
          value: '',
          errors: [],
          isValid: true,
        },
      },
    } as FormData,
  }

const createProfileSlice = createSlice({
  name: CREATE_PROFILE_REDUCER,
  initialState: initialCreateProfileReduxState,
  reducers: {
    updatePageIsLoading: (state, action: PayloadAction<boolean>) => {
      state.pageIsLoading = action.payload
    },
    updateErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload
    },
    updateUnmappedErrorOccurred: (state, action: PayloadAction<boolean>) => {
      state.unmappedErrorOccurred = action.payload
    },
    updateRetryUnmappedError: (state, action: PayloadAction<boolean>) => {
      state.retryUnmappedError = action.payload
    },
    updateInput: (state, action: PayloadAction<PanelInput>) => {
      const { currentPanelIndex } = state

      const [[key, value]] = Object.entries(action.payload) as [
        key: InputKeys,
        value: InputValue,
      ][]

      state.formData[currentPanelIndex][key] = {
        ...state.formData[currentPanelIndex][key],
        ...value,
      }
    },
    updateInputValue: (state, action: PayloadAction<object>) => {
      const { currentPanelIndex } = state
      const [[key, value]] = Object.entries(action.payload) as [
        key: InputKeys,
        value: InputValue['value'],
      ][]

      state.formData[currentPanelIndex][key].value = value
    },
    updateInputErrors: (state, action: PayloadAction<object>) => {
      const { currentPanelIndex } = state
      const [[key, value]] = Object.entries(action.payload) as [
        key: InputKeys,
        value: InputValue['errors'],
      ][]

      state.formData[currentPanelIndex][key].errors = value
    },
    updateInputValidity: (state, action: PayloadAction<object>) => {
      const { currentPanelIndex } = state
      const [[key, value]] = Object.entries(action.payload) as [
        key: InputKeys,
        value: boolean,
      ][]

      state.formData[currentPanelIndex][key].isValid = value
    },
    updateProfileDetails: (
      state,
      action: PayloadAction<Partial<ProfileDetails>>,
    ) => {
      state.profileDetails = { ...state.profileDetails, ...action.payload }
    },
    seedPanelInputValue: (
      state,
      action: PayloadAction<{
        panelIndex: number
        key: InputKeys
        value: InputValue['value']
      }>,
    ) => {
      const { panelIndex, key, value } = action.payload
      const panelInput = state.formData[panelIndex]?.[key]
      if (panelInput && panelInput.value === '') {
        panelInput.value = value
      }
    },
    updateCurrentPanelIndex: (state, action: PayloadAction<number>) => {
      state.currentPanelIndex = action.payload
    },
    updateProfileDetailsAreLoading: (state, action: PayloadAction<boolean>) => {
      state.profileDetailsAreLoading = action.payload
    },
  },
})

export const {
  updatePageIsLoading,
  updateErrorMessage,
  updateUnmappedErrorOccurred,
  updateRetryUnmappedError,
  updateInput,
  updateInputValue,
  updateInputErrors,
  updateInputValidity,
  updateProfileDetails,
  seedPanelInputValue,
  updateCurrentPanelIndex,
  updateProfileDetailsAreLoading,
} = createProfileSlice.actions

export default createProfileSlice.reducer
